import { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import api from '../services/api';
import CreatePreview from './CreatePreview';
import { wranglerError } from './errors';

/**
 * Upload quotas from a spreadsheet (design §5): download the template, fill it in, upload it.
 * Every row is checked first, by Ranger and then by Wrangler's preview; nothing is created until
 * all of them are valid.
 */
const SpreadsheetDialog = ({ surveyId, questions, onCreated, onClose }) => {
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(null); // 'template' | 'upload'
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null); // {fileName, quotas, problems, warnings}

  const downloadTemplate = async () => {
    setBusy('template');
    setError(null);
    try {
      const { data } = await api.get(`/surveys/${surveyId}/wrangler/quotas/template/`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'wrangler-quotas-template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(wranglerError(err, "The template couldn't be made."));
    } finally {
      setBusy(null);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    setError(null);
    setParsed(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/quotas/spreadsheet/`, form);
      setParsed({ fileName: file.name, ...data });
    } catch (err) {
      setError(wranglerError(err, "The spreadsheet couldn't be read."));
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const ready = Boolean(parsed && parsed.problems.length === 0 && parsed.quotas.length > 0);
  // Create's fields, without the spreadsheet row each came from (kept for messages).
  const drafts = useMemo(() => (ready ? parsed.quotas.map(({ row, ...quota }) => quota) : null), [ready, parsed]);
  const rows = ready ? parsed.quotas.map((q) => q.row) : [];

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>Upload quotas from a spreadsheet</Box>
        <IconButton onClick={onClose} aria-label="Close"><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {!ready && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Download the template and follow its Instructions sheet: one row per quota, with Name and Limit, and a column per
              question saying who to count. Pick an answer from a column&apos;s dropdown, or type several separated by commas,
              using each answer&apos;s text or reporting value. A blank column means any answer. Anything more complex is built in
              the quota editor.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
              <Button variant="outlined" startIcon={busy === 'template' ? <CircularProgress size={16} /> : <DownloadRoundedIcon />}
                onClick={downloadTemplate} disabled={Boolean(busy)} sx={{ textTransform: 'none' }}>
                Download the template
              </Button>
              <Button variant="contained" startIcon={busy === 'upload' ? <CircularProgress size={16} color="inherit" /> : <UploadFileRoundedIcon />}
                onClick={() => fileInput.current?.click()} disabled={Boolean(busy)} sx={{ textTransform: 'none' }}>
                {parsed ? 'Upload a corrected file' : 'Upload the filled-in file'}
              </Button>
              <input ref={fileInput} type="file" hidden accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                data-testid="spreadsheet-file" onChange={(e) => upload(e.target.files?.[0])} />
            </Box>
          </>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {parsed?.warnings?.map((w) => <Alert key={w} severity="info" sx={{ mb: 1 }}>{w}</Alert>)}

        {parsed && parsed.problems.length > 0 && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {parsed.fileName}: {parsed.problems.length} problem{parsed.problems.length !== 1 ? 's' : ''} to fix. Nothing has been
              created. Fix them in the spreadsheet and upload it again.
            </Alert>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow><TableCell>Row</TableCell><TableCell>Column</TableCell><TableCell>Problem</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {parsed.problems.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.row}</TableCell>
                      <TableCell>{p.column || '—'}</TableCell>
                      <TableCell>{p.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {ready && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {parsed.fileName}: every row reads correctly. Check them, then create.
            </Typography>
            <CreatePreview surveyId={surveyId} drafts={drafts} questions={questions} onCreated={onCreated} onDone={onClose}
              labelFor={(i) => `Row ${rows[i]} (${drafts[i]?.name})`} onBack={() => setParsed(null)} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SpreadsheetDialog;
